import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  flattenBulletPointsForTypewriter,
  formatMessage,
  parseBulletPoints,
  revealBulletPointsByCharCount,
} from '../utility/helpers';
import { useTypewriter } from './useTypewriter';

export type HunterMessageKind =
  | 'empty'
  | 'initial'
  | 'out_of_context'
  | 'general_info'
  | 'options'
  | 'suggestions'
  | 'finish'
  | 'default';

export type FinishSummaryEntry = {
  key: string;
  label: string;
  value: string;
};

export type RevealedFinishEntry = {
  key: string;
  /** Visible label text while typing (may be partial). */
  label: string;
  /** Visible value text while typing (may be partial). */
  value: string;
  /** True once the ": " separator has been reached. */
  showColon: boolean;
};

export type RevealedFinishSummary = {
  introText: string;
  entries: RevealedFinishEntry[];
  closingText: string;
  caretIn: 'intro' | 'entry' | 'closing' | null;
  caretEntryIndex: number | null;
};

type UseHunterProseTypewriterArgs = {
  message: any;
  chatId?: string | number | null;
  isLastMessage?: boolean;
  /** e.g. `.chat-with-job .mesgs` */
  scrollSelector: string;
  defaultInitial: string;
  /** When true, array `message.message` is treated as suggestion chips (Job/ICP). */
  supportSuggestions?: boolean;
};

const FINISH_INTRO = 'Finished! These are details based on your provided job info:-';
const FINISH_CLOSING = 'Let me know if you want to change or update anything?';
const SUGGESTION_CHIP_MS = 140;

const formatSummaryLabel = (key: string) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export function scrollChatToBottom(scrollSelector: string, options?: { force?: boolean }) {
  const container = document.querySelector(scrollSelector) as HTMLElement | null;
  if (!container) return;
  const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
  const nearBottom = distanceFromBottom < 80;
  if (!options?.force && !nearBottom) return;
  container.scrollTop = container.scrollHeight;
}

export function scrollChatToBottomAfterPaint(scrollSelector: string, options?: { force?: boolean }) {
  scrollChatToBottom(scrollSelector, options);
  requestAnimationFrame(() => {
    scrollChatToBottom(scrollSelector, options);
    requestAnimationFrame(() => scrollChatToBottom(scrollSelector, options));
  });
}

export function resolveHunterMessageKind(
  message: any,
  { supportSuggestions = false }: { supportSuggestions?: boolean } = {},
): HunterMessageKind {
  if (!message) return 'empty';
  if (message?.is_initial) return 'initial';
  if (message?.out_of_context === true) return 'out_of_context';
  if (message?.general_info === true) return 'general_info';
  if (
    message?.title &&
    (Array.isArray(message?.title) || (typeof message?.title === 'object' && message.title !== null))
  ) {
    return 'options';
  }
  if (supportSuggestions && message?.message && Array.isArray(message?.message)) {
    return 'suggestions';
  }
  if (message?.step === 'finish') return 'finish';
  return 'default';
}

const buildFinishEntries = (summary: Record<string, unknown> | null | undefined): FinishSummaryEntry[] => {
  if (!summary || typeof summary !== 'object') return [];
  return Object.entries(summary).map(([key, value]) => ({
    key,
    label: formatSummaryLabel(key),
    value: Array.isArray(value) ? value.join(', ') : String(value ?? ''),
  }));
};

export function revealFinishSummaryByCharCount(
  intro: string,
  entries: FinishSummaryEntry[],
  closing: string,
  charCount: number,
): RevealedFinishSummary {
  let remaining = Math.max(0, charCount);

  const introText = intro.slice(0, remaining);
  remaining = Math.max(0, remaining - intro.length);

  const revealedEntries: RevealedFinishEntry[] = [];
  let caretIn: RevealedFinishSummary['caretIn'] =
    remaining <= 0 && introText.length < intro.length ? 'intro' : null;
  let caretEntryIndex: number | null = null;

  for (let i = 0; i < entries.length; i += 1) {
    if (remaining <= 0) break;
    const { key, label, value } = entries[i];
    const full = `${label}: ${value}`;
    const visible = full.slice(0, remaining);
    let visibleLabel = '';
    let visibleValue = '';
    let showColon = false;

    if (visible.length <= label.length) {
      visibleLabel = visible;
    } else {
      visibleLabel = label;
      showColon = true;
      visibleValue = visible.slice(label.length + 2); // skip ": "
    }

    revealedEntries.push({
      key,
      label: visibleLabel,
      value: visibleValue,
      showColon,
    });
    remaining -= full.length;
    if (remaining < 0 || visible.length < full.length) {
      caretIn = 'entry';
      caretEntryIndex = i;
      remaining = 0;
      break;
    }
  }

  const closingText = closing.slice(0, Math.max(0, remaining));
  if (!caretIn && closingText.length > 0 && closingText.length < closing.length) {
    caretIn = 'closing';
  }
  if (!caretIn && introText.length > 0 && introText.length < intro.length) {
    caretIn = 'intro';
  }

  return { introText, entries: revealedEntries, closingText, caretIn, caretEntryIndex };
}

/**
 * Shared prose typewriter used by general / job / ICP Hunter chats.
 */
export function useHunterProseTypewriter({
  message,
  chatId,
  isLastMessage,
  scrollSelector,
  defaultInitial,
  supportSuggestions = false,
}: UseHunterProseTypewriterArgs) {
  const messageKind = useMemo(
    () => resolveHunterMessageKind(message, { supportSuggestions }),
    [message, supportSuggestions],
  );

  const suggestionItems = useMemo(() => {
    if (messageKind !== 'suggestions' || !Array.isArray(message?.message)) return [] as string[];
    return message.message.filter(
      (text: unknown) =>
        typeof text === 'string' && !text.toLowerCase().includes('what makes you a great candidate'),
    ) as string[];
  }, [message, messageKind]);

  const suggestionIntro = useMemo(() => {
    if (messageKind !== 'suggestions') return '';
    const hasTargetSection =
      typeof message?.section === 'string' ||
      (typeof message?.title === 'string' &&
        typeof message?.title !== 'object' &&
        !Array.isArray(message?.title));
    if (hasTargetSection) {
      const sectionLabel = typeof message?.section === 'string' ? message.section : message?.title;
      return `Here are ${suggestionItems.length} ${sectionLabel} suggestions:`;
    }
    return 'Here are some suggestions:';
  }, [message, messageKind, suggestionItems.length]);

  const finishEntries = useMemo(
    () => (messageKind === 'finish' ? buildFinishEntries(message?.summary) : []),
    [message, messageKind],
  );

  const fullMessageText = useMemo(() => {
    if (!message) return '';

    if (messageKind === 'initial') {
      return message?.message || defaultInitial;
    }

    if (messageKind === 'out_of_context') {
      return Array.isArray(message?.message)
        ? String(message.message[0] || '')
        : String(
            message?.message ||
              "Sorry, that's out of my context. I can only help with job descriptions and candidate requirements.",
          );
    }

    if (messageKind === 'general_info') {
      return Array.isArray(message?.message)
        ? String(message.message[0] || '')
        : String(message?.message || "Here's the information you requested.");
    }

    if (messageKind === 'options') {
      return message?.message
        ? formatMessage(message.message)
        : 'Please choose from the following options:';
    }

    if (messageKind === 'suggestions') {
      return suggestionIntro;
    }

    if (messageKind === 'finish') {
      const entryLines = finishEntries.map((entry) => `${entry.label}: ${entry.value}`);
      return [FINISH_INTRO, ...entryLines, FINISH_CLOSING].join('');
    }

    if (messageKind === 'empty') {
      return '';
    }

    const messageText =
      message?.step === 'distance'
        ? `${formatMessage(message?.message)} Distances are calculated using your office location to ensure precision.`
        : formatMessage(message?.message);

    return messageText || '';
  }, [message, messageKind, defaultInitial, suggestionIntro, finishEntries]);

  const onProgress = useCallback(() => {
    scrollChatToBottomAfterPaint(scrollSelector);
  }, [scrollSelector]);

  const parsedBullets = useMemo(() => {
    if (!fullMessageText) return null;
    if (
      messageKind === 'finish' ||
      messageKind === 'empty' ||
      messageKind === 'initial' ||
      messageKind === 'suggestions'
    ) {
      return null;
    }
    return parseBulletPoints(fullMessageText);
  }, [messageKind, fullMessageText]);

  const typewriterSource = useMemo(() => {
    if (parsedBullets?.bulletItems?.length) {
      return flattenBulletPointsForTypewriter(parsedBullets);
    }
    return fullMessageText;
  }, [parsedBullets, fullMessageText]);

  const shouldType = !!isLastMessage && messageKind !== 'empty';

  const { displayedText, isComplete, skippedAnimation } = useTypewriter(typewriterSource, {
    enabled: shouldType,
    messageKey: chatId,
    onProgress,
  });

  const revealedBullets = useMemo(() => {
    if (!parsedBullets?.bulletItems?.length) return null;
    const charCount = shouldType ? displayedText.length : typewriterSource.length;
    return revealBulletPointsByCharCount(parsedBullets, charCount);
  }, [parsedBullets, shouldType, displayedText.length, typewriterSource.length]);

  const revealedFinish = useMemo(() => {
    if (messageKind !== 'finish') return null;
    const charCount = shouldType ? displayedText.length : typewriterSource.length;
    return revealFinishSummaryByCharCount(FINISH_INTRO, finishEntries, FINISH_CLOSING, charCount);
  }, [messageKind, shouldType, displayedText.length, typewriterSource.length, finishEntries]);

  const [visibleSuggestionCount, setVisibleSuggestionCount] = useState(() =>
    isLastMessage ? 0 : suggestionItems.length,
  );

  useEffect(() => {
    if (messageKind !== 'suggestions') {
      setVisibleSuggestionCount(0);
      return;
    }
    if (!isLastMessage || !shouldType || skippedAnimation) {
      setVisibleSuggestionCount(suggestionItems.length);
      return;
    }
    if (!isComplete) {
      setVisibleSuggestionCount(0);
      return;
    }

    setVisibleSuggestionCount(0);
    if (suggestionItems.length === 0) return undefined;

    let count = 0;
    const timer = window.setInterval(() => {
      count += 1;
      setVisibleSuggestionCount(count);
      scrollChatToBottomAfterPaint(scrollSelector);
      if (count >= suggestionItems.length) {
        window.clearInterval(timer);
      }
    }, SUGGESTION_CHIP_MS);

    return () => window.clearInterval(timer);
  }, [
    messageKind,
    isLastMessage,
    shouldType,
    skippedAnimation,
    isComplete,
    suggestionItems.length,
    chatId,
    scrollSelector,
  ]);

  const suggestionsRevealComplete =
    messageKind !== 'suggestions' ||
    !isLastMessage ||
    !shouldType ||
    (isComplete && visibleSuggestionCount >= suggestionItems.length);

  const hasOptions =
    (Array.isArray(message?.title) && message.title.length > 0) ||
    (!Array.isArray(message?.title) &&
      message?.title &&
      typeof message?.title === 'object' &&
      message.title !== null);

  const showOptions = hasOptions && (!isLastMessage || isComplete);
  const showStructuredFinish =
    messageKind === 'finish' && (!isLastMessage || !shouldType || isComplete);

  useEffect(() => {
    if (!isLastMessage) return;
    scrollChatToBottomAfterPaint(scrollSelector);
    if (!isComplete && !showOptions && !suggestionsRevealComplete) return;
    const timer = window.setTimeout(() => scrollChatToBottomAfterPaint(scrollSelector), 120);
    return () => window.clearTimeout(timer);
  }, [
    isLastMessage,
    displayedText,
    isComplete,
    showOptions,
    showStructuredFinish,
    suggestionsRevealComplete,
    visibleSuggestionCount,
    revealedBullets?.bulletItems?.length,
    revealedBullets?.introText,
    revealedBullets?.closingText,
    revealedFinish?.entries?.length,
    revealedFinish?.introText,
    revealedFinish?.closingText,
    scrollSelector,
  ]);

  const renderCaret = (active: boolean) =>
    active && shouldType && !isComplete ? (
      <span className="chat-typewriter-caret" aria-hidden="true" />
    ) : null;

  return {
    messageKind,
    displayedText,
    isComplete,
    shouldType,
    revealedBullets,
    revealedFinish,
    showStructuredFinish,
    showOptions,
    suggestionItems,
    visibleSuggestionCount,
    suggestionsRevealComplete,
    suggestionIntro,
    renderCaret,
  };
}
